import { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useRbacHook } from "../../../hooks/useRbacHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import { TRole } from "../../../apis/rbac/rbac.interfaces";

interface DeleteRoleDialogProps {
  open: boolean;
  role: TRole | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const DeleteRoleDialog: FC<DeleteRoleDialogProps> = ({
  open,
  role,
  onClose,
  onSuccess,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const { deleteRole, isDeletingRole } = useRbacHook();

  const handleConfirm = async () => {
    if (!role?.id || !selectedInstitute?.id) return;
    try {
      await deleteRole(role.id);
      logger.show_success("Role deleted successfully");
      onClose();
      onSuccess?.();
    } catch {
      logger.show_error("Failed to delete role. Please try again.");
    }
  };

  const handleClose = () => {
    if (!isDeletingRole) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="delete-role-dialog"
    >
      <DialogTitle data-testid="delete-role-dialog-title">
        Delete Role
      </DialogTitle>
      <DialogContent data-testid="delete-role-dialog-content">
        <DialogContentText>
          Are you sure you want to delete role &quot;{role?.name}&quot;? This
          action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions data-testid="delete-role-dialog-actions">
        <Button
          onClick={handleClose}
          disabled={isDeletingRole}
          data-testid="cancel-delete-role-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeletingRole}
          startIcon={
            isDeletingRole ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteIcon />
            )
          }
          data-testid="confirm-delete-role-button"
        >
          {isDeletingRole ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteRoleDialog;
